import { forwardRef, memo, useMemo, useRef } from 'react'
import { cva } from 'class-variance-authority'
import { Markdown, type MarkdownProps } from '@highlight/ui'
import { breakCellComponents, mathRehypePlugins, mathRemarkPlugins } from '@renderer/components/markdown-extras'
import { CodeBlock, ShortcutIcon } from '../../../../components'
import { createFlowingBlockComponents } from '../../../../components/flowing-block-components'
import { ImageComponent } from './message-image'
import { chunkifyForReveal } from './reveal-chunks'
import type { MentionableItem } from '../../../../types'

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function looksLikeMath(content: string): boolean {
  if (/\\[a-zA-Z]+/.test(content)) return true
  if (/[\^_]/.test(content)) return true
  if (/[{}]/.test(content)) return true
  return false
}

export function preprocessMathDollars(input: string): string {
  const codeSegments: Array<string> = []
  const mathSegments: Array<string> = []
  let codeIndex = 0
  let mathIndex = 0

  let text = input.replace(/```[\s\S]+?```|`[^`]+?`/g, (match) => {
    codeSegments.push(match)
    return `__CODE_${codeIndex++}__`
  })

  text = text.replace(/\$\$([\s\S]+?)\$\$/g, (match) => {
    mathSegments.push(match)
    return `__MATH_${mathIndex++}__`
  })

  text = text.replace(/\$([^$\n]+?)\$/g, (match, content) => {
    if (looksLikeMath(content)) {
      mathSegments.push(match)
      return `__MATH_${mathIndex++}__`
    }
    return `\\$${content}\\$`
  })

  text = text.replace(/(?<!\\)\$/g, '\\$')
  text = text.replace(/__MATH_(\d+)__/g, (_, i) => mathSegments[parseInt(i)])
  text = text.replace(/__CODE_(\d+)__/g, (_, i) => codeSegments[parseInt(i)])

  return text
}

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const renderMentionText = (mentions: Array<MentionableItem>, text: string) => {
  if (!mentions?.length) return text

  // Slash mentions only match at start-of-text or after whitespace, mirroring validation logic
  // so URLs/file paths containing the label aren't falsely highlighted.
  const mentionRegex = new RegExp(
    `(${mentions
      .map((item) => {
        const escaped = escapeRegex(item.label)
        return `@${escaped}|(?<=^|\\s)/${escaped}`
      })
      .join('|')})`,
    'g',
  )
  const parts = text.split(mentionRegex)

  return parts.map((part, index) => {
    const mention = mentions.find((item) => `@${item.label}` === part || `/${item.label}` === part)
    if (mention) {
      const hasIcon = mention.type === 'shortcut' && mention.images?.image && mention.images?.userImages
      return (
        <span key={index} className={'text-info-subtle rounded-lg font-medium'}>
          {hasIcon && (
            <ShortcutIcon
              image={mention.images!.image!}
              extension={mention.images!.userImages!.file_extension}
              size={22}
              style={{ marginBottom: -6, marginInlineEnd: 2 }}
            />
          )}
          {hasIcon ? part.slice(1) : part}
        </span>
      )
    }
    return part
  })
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

export const messageStyles = cva(
  '!selection:text-foreground relative flex w-full max-w-full flex-col gap-2 bg-transparent',
  {
    variants: {
      role: {
        user: 'text-foreground border-t self-end pt-6 pb-4 first:border-none first:pt-3 [&_p]:mb-0 px-4',
        assistant: 'self-start pb-4 px-5 app:px-4',
        tool: 'text-foreground-secondary self-start py-4 px-4',
      },
      isSubmessage: {
        true: 'px-0',
        false: '',
      },
      sequential: {
        sequential: 'pt-0',
        default: '',
      },
      hasNoAttachment: {
        true: 'gap-0',
        false: '',
      },
    },
    defaultVariants: {
      role: 'assistant',
      isSubmessage: false,
      sequential: 'default',
      hasNoAttachment: false,
    },
  },
)

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Markdown links always leave the app. `window.open` is the renderer path the
 * main process handles. Its window-open handler routes the URL through
 * `shell.openExternal` (WindowManager.bindOpenUrlHandlers).
 */
export const LinkComponent = ({ children, href }: React.PropsWithChildren<{ href?: string }>) => {
  const url = typeof href === 'string' ? href.trim() : ''

  // A bare `[label]()` has no destination, so render the label rather than an
  // anchor that goes nowhere.
  if (!url) return <>{children}</>

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    // Keep the click off any enclosing row/message handler.
    e.stopPropagation()
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="cursor-pointer break-words"
    >
      {children}
    </a>
  )
}

/**
 * react-markdown wraps every fenced block in `<pre><code>`. The `code` slot
 * below renders `CodeBlock`, which is already the card, and `Markdown`'s own
 * `pre` would draw a second one around it. Without the wrapper the card is the
 * block, so `.markdown` spaces it like any other.
 */
export const PreComponent = ({ children }: React.PropsWithChildren) => <>{children}</>

// ---------------------------------------------------------------------------
// MessageMarkdown
// ---------------------------------------------------------------------------

interface MessageMarkdownProps {
  content: string
  messageId: string
  mentions?: Array<MentionableItem>
  shouldAnimate?: boolean
  /** Wrap text into inert per-word chunk spans for the post-stream reveal
   * (final-reveal.tsx animates them imperatively). Structural only — without
   * a reveal pass the spans render as plain text. */
  revealChunks?: boolean
  size?: MarkdownProps['size']
  className?: string
}

/** The `.markdown` element: its direct children are the message's blocks, which the reveal pass walks. */
export const MessageMarkdown = memo(
  forwardRef<HTMLDivElement, MessageMarkdownProps>(function MessageMarkdown(
    { content, messageId, mentions, shouldAnimate = false, revealChunks = false, size, className },
    ref,
  ) {
    // Read through a ref so a turn-end flip of `shouldAnimate` doesn't change CodeComponent's identity and remount code blocks.
    const shouldAnimateRef = useRef(shouldAnimate)
    shouldAnimateRef.current = shouldAnimate

    const CodeComponent = useMemo(() => {
      return function CodeComponent({
        inline,
        className,
        children,
        ...props
      }: React.ComponentProps<'code'> & { inline?: boolean }) {
        const match = /language-(\w+)/.exec(className || '')
        const isStringWithNewlines = typeof children === 'string' && children.includes('\n')
        const language = match?.[1] ?? 'plaintext'

        if ((!inline && match) || isStringWithNewlines) {
          return (
            <CodeBlock messageId={messageId} language={language} shouldAnimate={shouldAnimateRef.current}>
              {children}
            </CodeBlock>
          )
        }

        return (
          <code className={className} {...props}>
            {children}
          </code>
        )
      }
    }, [messageId])

    const flowingComponents = useMemo(
      () =>
        createFlowingBlockComponents({
          shouldAnimate,
          renderText: mentions ? (text) => renderMentionText(mentions, text) : undefined,
          chunkify: revealChunks ? chunkifyForReveal : undefined,
        }),
      [mentions, shouldAnimate, revealChunks],
    )

    const components = useMemo(() => {
      return {
        ...flowingComponents,
        a: LinkComponent,
        code: CodeComponent,
        pre: PreComponent,
        ...breakCellComponents,
        img: ImageComponent,
      }
    }, [flowingComponents, CodeComponent])

    // Six chained regex passes over the whole message body. For the streaming
    // message this ran per token over text growing to several KB — O(n²) across a
    // reply. The content string is the only input, so memoize on it.
    const processedContent = useMemo(() => preprocessMathDollars(content), [content])

    return (
      <Markdown
        ref={ref}
        size={size}
        ladder="compact"
        className={className}
        remarkPlugins={mathRemarkPlugins}
        rehypePlugins={mathRehypePlugins}
        components={components}
      >
        {processedContent}
      </Markdown>
    )
  }),
)

MessageMarkdown.displayName = 'MessageMarkdown'
